import { WeeklyOpeningHours, WeekdayName } from '@shared/models';

export interface NextOpening {
  date: Date;
  dayOffset: number;
  dayIndexMonFirst: number;
  openTime: string;
}

export interface StoreStatus {
  isOpenNow: boolean;
  currentSlotEnd: string | null;
  nextOpening: NextOpening | null;
}

const WEEK_DAYS: WeekdayName[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

interface ParsedSlot {
  start: number;
  end: number;
}

const parseTime = (value: string): number | null => {
  const parts = value.split(':');
  if (parts.length !== 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isInteger(h) || !Number.isInteger(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
};

const formatMinutes = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
};

const parseDaySlots = (weekly: WeeklyOpeningHours, day: WeekdayName): ParsedSlot[] => {
  const entry = weekly[day];
  if (!entry?.isOpen) return [];
  return entry.timeSlots
    .flatMap(({ start, end }) => {
      const s = parseTime(start);
      const e = parseTime(end);
      return s !== null && e !== null && s < e ? [{ start: s, end: e }] : [];
    })
    .sort((a, b) => a.start - b.start);
};

const buildNextOpening = (reference: Date, dayOffset: number, minutes: number): NextOpening => {
  const date = new Date(reference);
  date.setDate(reference.getDate() + dayOffset);
  date.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return {
    date,
    dayOffset,
    dayIndexMonFirst: (date.getDay() + 6) % 7,
    openTime: formatMinutes(minutes),
  };
};

export const getStoreStatus = (weekly: WeeklyOpeningHours, now: Date = new Date()): StoreStatus => {
  const todayKey = WEEK_DAYS[now.getDay()]!;
  const nowMin = now.getHours() * 60 + now.getMinutes();

  const todaySlots = parseDaySlots(weekly, todayKey);
  for (const slot of todaySlots) {
    if (nowMin >= slot.start && nowMin < slot.end) {
      return { isOpenNow: true, currentSlotEnd: formatMinutes(slot.end), nextOpening: null };
    }
  }

  const futureToday = todaySlots.find((slot) => slot.start > nowMin);
  if (futureToday) {
    return { isOpenNow: false, currentSlotEnd: null, nextOpening: buildNextOpening(now, 0, futureToday.start) };
  }

  for (let offset = 1; offset <= 7; offset++) {
    const dayKey = WEEK_DAYS[(now.getDay() + offset) % 7]!;
    const slots = parseDaySlots(weekly, dayKey);
    if (slots.length > 0) {
      return { isOpenNow: false, currentSlotEnd: null, nextOpening: buildNextOpening(now, offset, slots[0]!.start) };
    }
  }

  return { isOpenNow: false, currentSlotEnd: null, nextOpening: null };
};
